import './index.scss';

import React from 'react';
import Scrollbar from 'react-scrollbars-custom';
import ScrollIntoView from 'react-scroll-into-view';
import { format as formatDate } from 'date-fns';
import { connect } from 'react-redux';
import { Layout, Button, Modal } from 'antd';
import { LeftOutlined, CloseOutlined } from '@ant-design/icons';
import { bookingActions } from '../../actions/booking';
import { BookingStep } from '../../constants';
import getNested from '../../utils/getNested';
import { extractServices, getBookingDuration } from '../../utils/bookingUtil';
import { User } from '../../types/user';
import { Stylist, StylistType, FetchStylistOptions, createDefaultStylist, FetchDatesOptions, FetchTimesOptions } from '../../types/stylist';
import { Service, ServiceGroup } from '../../types/service';
import { Configuration } from '../../types/configuration';
import { salonActions } from '../../actions/salon';
import BookingServices from '../../components/BookingServices';
import BookingDetailHeader from '../../components/BookingDetailHeader';
import BookingSummary from '../../components/BookingSummary';
import BookingDateTime from '../../components/BookingDateTime';
import UserProfile from '../../components/UserProfile';

interface Props {
  users: User[];
  salonId: number;
  stylistId: number;
  stylistDate: any;
  stylistTime: string;
  bookingId: number;
  currentBooking: any;
  accessToken: string;
  activeUserId: number;
  bookingSettings: Configuration;
  updateStep: (step: number) => void;
  updateUsersList: (users: User[]) => void;
  storeActiveUser: (userId: number) => void;
  fetchUserProfile: (options) => Promise<any>;
  storeBookingNotes: (notes: string) => void;

  storeActiveRootCateMobile: (cateId: string | null) => void;
  storeActiveRootCate: (cateId: string | null) => void;
  storeActiveSubCate: (cateId: string | null) => void;

  storeStylists: (stylists: Stylist[]) => void;
  fetchStylists: (options: FetchStylistOptions, services?: any[]) => Promise<Stylist[]>;
  storeMappedStylists: (options: Record<number, Stylist[]>) => void;

  storeStylistSettings: (settings: any) => void;
  fetchStylistSettings: (options: FetchDatesOptions, services?: any[]) => Promise<any>;

  storeAvailableTimes: (times: any[]) => void;
  fetchAvailableTimes: (options: FetchTimesOptions, services?: any[]) => Promise<any>;

  storeSalonServices: (serviceGroups: ServiceGroup[]) => void;
  fetchSalonServices: (salonId: number, stylistId?: number) => Promise<ServiceGroup[]>;
}

interface States {
  isFetchingServices: boolean;
  isFetchingStylists: boolean;
  isFetchingDate: boolean;
  isFetchingTime: boolean;
  userProfile: any;
  isFetchingProfile: boolean;
}

class BookingDetail extends React.PureComponent<Props, States> {
  state;

  constructor(props) {
    super(props);

    this.state = {
      isFetchingServices: true,
      isFetchingStylists: true,
      isFetchingDate: true,
      isFetchingTime: true,
      userProfile: null,
      isFetchingProfile: false,
    }
  }

  async componentDidMount() {
    const {
      salonId,
      stylistId,
      storeSalonServices,
      fetchSalonServices,
      bookingId,
      currentBooking,
      storeBookingNotes,
    } = this.props;
    if (salonId) {
      const services = await fetchSalonServices(salonId, stylistId);
      storeSalonServices(services);

      // map new data for current booking & refresh data
      if (bookingId && currentBooking) {
        this.setState({ isFetchingServices: false });

        storeBookingNotes(currentBooking.bookingNotes);
        this.refreshBookingData(extractServices(services));
      } else {
        this.setState({
          isFetchingServices: false,
          isFetchingStylists: false,
          isFetchingDate: false,
          isFetchingTime: false,
        });
      }
    }
  }

  private getAvailableStylists(selectedService: Service, availableStylists: any[] = []) {
    const { users, activeUserId } = this.props;
    let excludedStylistIds: number[] = [];
    users
      .filter(user => {
        return user.id !== activeUserId && user.selectedServices.length > 0;
      })
      .forEach(user => {
        const matchedService = user.selectedServices.find(service => service.id === selectedService.id);
        if (matchedService && [StylistType.NoUser, StylistType.NoPreference].indexOf(matchedService.stylistId) === -1) {
          excludedStylistIds = excludedStylistIds.concat([matchedService.stylistId]);
        }
      });


    return availableStylists.filter(stylist => {
      const isNoPreference = stylist.id === StylistType.NoPreference;
      const canPerform = stylist.canBook && !stylist.isOff &&  stylist.availableServices.find(service => service.id === selectedService.id);

      return (isNoPreference || canPerform) && excludedStylistIds.indexOf(stylist.id) === -1;
    });
  }

  private refreshBookingData(allServices: Service[]) {
    const activeUser = this.getActiveUser();

    if (activeUser) {
      const { users, updateUsersList } = this.props;

      // mapping services
      const newUsers = [...users].map((user: User) => {
        const userServices = getNested(user, 'selectedServices', []) || [];
        const selectedServices = userServices
          .filter(service => allServices.find(s => s.id === service.id))
          .map(service => {
            const newService = allServices.find(s => s.id === service.id);
            return { ...newService, ...service };
          });
        return { ...user, selectedServices };
      });
      updateUsersList([...newUsers]);

      setTimeout(() => {
        this.fetchAvailableStylists();
        this.fetchAvailableDates();
        this.fetchAvailableTimes();
      }, 300);
    }
  }

  private async fetchAvailableStylists() {
    const activeUser = this.getActiveUser() as any;
    if (activeUser) {
      this.setState({ isFetchingStylists: true });

      try {
        const {
          users,
          salonId,
          bookingId,
          accessToken,
          stylistId,
          bookingSettings,
          fetchStylists,
          storeStylists,
          storeMappedStylists,
          updateUsersList,
        } = this.props;
        const { selectedDate, selectedTime } = activeUser;
        const fetchOptions = {
          salonId,
          date: selectedDate ? formatDate(selectedDate, 'yyyy-MM-dd') : undefined,
          time: selectedTime ? String(selectedTime) : undefined,
          bookingId,
          accessToken,
        };
        const userServices = getNested(activeUser, 'selectedServices', []);

        const stylists = await fetchStylists(fetchOptions, userServices);
        const availableStylists = stylists.filter(stylist => stylist.availableServices.length > 0);
        const defaultStylist = createDefaultStylist(userServices);
        const allStylists = (bookingSettings.hasDefaultStylist ? [defaultStylist] : []).concat(availableStylists);
        storeStylists(allStylists);
        storeMappedStylists({
          [activeUser.id]: allStylists,
        });

        // update stylist in selected services from url stylist
        if (!!stylistId) {
          const initialStylist = availableStylists.find(stylist => stylist.id === stylistId);

          if (initialStylist) {
            let needRefreshStlists = false;
            const canPerformServices = initialStylist.availableServices.map(service => service.id);
            const newUsers = [...users].map((user: User) => {
              if (user.canSelectStylist) {
                const selectedServices = getNested(user, 'selectedServices', [])
                  .map(service => {
                    if ((!service.stylistId || service.stylistId === StylistType.NoUser) && canPerformServices.indexOf(service.id) > -1) {
                      const availableMappedStylists = this.getAvailableStylists(service, allStylists);
                      const selectedStylist = availableMappedStylists.find(stylist => stylist.id === Number(stylistId));

                      if (selectedStylist) {
                        needRefreshStlists = true;

                        return {
                          ...service,
                          stylistId: selectedStylist.id,
                          stylistName: selectedStylist.name,
                        };
                      }

                      return service;
                    }

                    return service;
                  });

                return {
                  ...user,
                  selectedServices,
                };
              }

              return user;
            });

            if (needRefreshStlists) {
              updateUsersList([...newUsers]);

              if (activeUser.canSelectDateTime && this.validateStylists()) {
                this.fetchAvailableDates();
                this.fetchAvailableTimes();
              }
            }
          }
        }
      } catch(error) {
        console.log('FetchStylists.Error', error);
      }

      this.setState({ isFetchingStylists: false });
    }
  }

  private async fetchAvailableDates() {
    const activeUser = this.getActiveUser();
    if (activeUser) {
      this.setState({ isFetchingDate: true });

      try {
        const userServices = getNested(activeUser, 'selectedServices', []);
        const { salonId, bookingId, accessToken, fetchStylistSettings, storeStylistSettings } = this.props;
        const fetchOptions = { salonId, bookingId, accessToken };
        const stylistSettings = await fetchStylistSettings(fetchOptions, userServices);
        storeStylistSettings(stylistSettings);
      } catch(error) {
        console.log('FetchDates.Error', error);
      }

      this.setState({ isFetchingDate: false });
    }
  }

  private async fetchAvailableTimes() {
    const activeUser = this.getActiveUser();
    if (activeUser) {
      this.setState({ isFetchingTime: true });

      try {
        const { selectedDate, selectedTime, selectedServices } = activeUser;
        const { users, salonId, bookingId, accessToken, bookingSettings, fetchAvailableTimes, storeAvailableTimes } = this.props;
        const {
          canBookAnyTime,
          schedules,
          minimumAdvancedBookingHours: minHours,
        } = bookingSettings;
        const fetchOptions = {
          salonId,
          bookingId,
          accessToken,
          canBookAnyTime,
          minHours,
          duration: getBookingDuration(users),
          schedule: schedules[formatDate(selectedDate, 'EEEE')],
          selectedTime: bookingId ? selectedTime : undefined,
          selectedDate: selectedDate ? formatDate(selectedDate, 'yyyy-MM-dd') : undefined,
        };

        const availableTimes = await fetchAvailableTimes(fetchOptions, selectedServices);
        storeAvailableTimes(availableTimes);
      } catch(error) {
        console.log('FetchTimes.Error', error);
      }

      this.setState({ isFetchingTime: false });
    }
  }

  setActiveUser(userId: number) {
    const {
      users,
      storeActiveRootCateMobile,
      storeActiveRootCate,
      storeActiveSubCate,
      storeActiveUser,
    } = this.props;

    // reset all active cates
    storeActiveUser(userId);
    storeActiveRootCateMobile(null);
    storeActiveRootCate(null);
    storeActiveSubCate(null);

    // if there are selected services then need to fetch and validate selected stylists
    setTimeout(() => {
      const activeUserIndex = users.findIndex(user => user.id === userId);
      if (activeUserIndex > 0 && this.validateServices()) {
        this.fetchAvailableStylists();
      }
    }, 300);
  }

  getActiveUser() {
    const { users, activeUserId } = this.props;
    return users.find(user => user.id === activeUserId);
  }

  async fetchUserProfile(options) {
    this.setState({ isFetchingProfile: true });

    try {
      const { fetchUserProfile } = this.props;
      const userProfile = await fetchUserProfile(options);
      this.setState({ isFetchingProfile: false, userProfile });
    } catch(error) {
      console.log('FetchProfile.Error', error);
      this.setState({ isFetchingProfile: false });
    }
  }

  validateServices() {
    const activeUserServices = getNested(this.getActiveUser(), 'selectedServices', []);
    return activeUserServices.length > 0;
  }

  updateUserServices(selectedService: Service) {
    const activeUser = this.getActiveUser();

    if (activeUser) {
      const { users, updateUsersList } = this.props;
      let userServices = getNested(activeUser, 'selectedServices', []);
      const changedServiceIndex = userServices.findIndex(service => service.id === selectedService.id);

      if (changedServiceIndex >= 0) {
        userServices = userServices.filter(service => service.id !== selectedService.id);
      } else {
        userServices = userServices.concat([selectedService]);
      }

      const newUsers = [...users].map((user: User) => {
        return user.id === activeUser.id ? {...user, selectedServices: userServices } : user;
      });
      updateUsersList([...newUsers]);
    }
  }

  onSelectService(selectedService: Service) {
    const activeUser = this.getActiveUser();

    if (activeUser) {
      const {
        users,
        stylistId,
        stylistDate,
        stylistTime,
        currentBooking,
        updateUsersList,
      } = this.props;
      const { bookingClients } = currentBooking || { bookingClients: [] };
      let userServices = getNested(activeUser, 'selectedServices', []);
      const changedServiceIndex = userServices.findIndex(service => service.id === selectedService.id);

      if (changedServiceIndex >= 0) {
        userServices = userServices.filter(service => service.id !== selectedService.id);
      } else {
        if (!activeUser.canSelectStylist) {
          selectedService.stylistId = StylistType.NoPreference;
          selectedService.stylistName = 'No Preference';
        }

        userServices = userServices.concat([selectedService]);
      }

      const newUsers = [...users].map((user: User) => {
        if (user.id === activeUser.id) {
          const currentBookingDate = (bookingClients[0] || {}).selectedDate;
          const currentBookingTime = (bookingClients[0] || {}).selectedTime;

          return {
            ...user,
            selectedServices: userServices,
            selectedDate: !!stylistId && !!stylistDate ? stylistDate : (currentBookingDate || null),
            selectedTime: !!stylistId && !!stylistTime ? stylistTime : (currentBookingTime || null),
          };
        }

        return user;
      });
      updateUsersList([...newUsers]);

      // Fetch available stylists
      if (activeUser.canSelectStylist) {
        this.setState({ isFetchingStylists: true });
        setTimeout(() => this.fetchAvailableStylists(), 300);
      }
    }
  }

  validateStylists() {
    const activeUserServices = getNested(this.getActiveUser(), 'selectedServices', []);
    const hasInvalidStylist = activeUserServices
      .find((service: Service) => service.stylistId === StylistType.NoUser);
    return !hasInvalidStylist;
  }

  onSelectStylist(selectedService: Service) {
    const activeUser = this.getActiveUser();

    if (activeUser) {
      const {
        users,
        stylistId,
        stylistDate,
        stylistTime,
        currentBooking,
        updateUsersList,
      } = this.props;
      const { bookingClients } = currentBooking || { bookingClients: [] };
      const userServices = getNested(activeUser, 'selectedServices', []);
      const selectedServices = [...userServices].map(service => {
        return service.id === selectedService.id ? selectedService : service;
      });

      const newUsers = [...users].map((user: User) => {
        if (user.id === activeUser.id) {
          const currentBookingDate = (bookingClients[0] || {}).selectedDate;
          const currentBookingTime = (bookingClients[0] || {}).selectedTime;

          return {
            ...user,
            selectedServices,
            selectedDate: !!stylistId && !!stylistDate ? stylistDate : (currentBookingDate || null),
            selectedTime: !!stylistId && !!stylistTime ? stylistTime : (currentBookingTime || null),
          };
        }

        return user;
      });
      updateUsersList([...newUsers]);

      // Fetch available dates
      if (this.validateStylists()) {
        setTimeout(() => this.fetchAvailableDates(), 300);
      }
    }
  }

  validateDateTime() {
    const { selectedDate, selectedTime } = this.getActiveUser() as any;
    return !!selectedDate && !!selectedTime;
  }

  onSelectDate(date: Date) {
    const activeUser = this.getActiveUser();

    if (activeUser && activeUser.selectedDate !== date) {
      const { users , updateUsersList } = this.props;
      const newUsers = [...users].map((user: User) => {
        if (user.id !== activeUser.id) {
          return {
            ...user,
            selectedServices: (user.selectedServices).map(service => {
              return {
                ...service,
                stylistId: StylistType.NoUser,
                stylistName: '',
              };
            }),
            selectedDate: date,
            selectedTime: null,
          };
        }

        return { ...user, selectedDate: date, selectedTime: null };
      });
      updateUsersList([...newUsers]);

      // Fetch available times
      this.setState({ isFetchingTime: true });
      setTimeout(() => this.fetchAvailableTimes(), 300);
    }
  }

  onSelectTime(time: string) {
    const activeUser = this.getActiveUser();

    if (activeUser && activeUser.selectedTime !== time) {
      const { users , updateUsersList } = this.props;
      const newUsers = [...users].map((user: User) => {
        if (user.id !== activeUser.id) {
          return {
            ...user,
            selectedServices: (user.selectedServices).map(service => {
              return {
                ...service,
                stylistId: StylistType.NoUser,
                stylistName: '',
              };
            }),
            selectedTime: time,
          };
        }

        return { ...user, selectedTime: time };
      });
      updateUsersList([...newUsers]);
    }
  }

  gotoSelectUsers() {
    const { updateStep } = this.props;
    updateStep(BookingStep.Users);
  }

  gotoCheckout() {
    const { updateStep } = this.props;
    updateStep(BookingStep.Confirmation);
  }

  renderContent() {
    const { activeUserId } = this.props;
    const activeUser = this.getActiveUser();
    const { selectedDate, selectedTime } = activeUser as any;
    const {
      isFetchingServices,
      isFetchingStylists,
      isFetchingDate,
      isFetchingTime,
    } = this.state;

    return (
      <Scrollbar>
        <div className="booking-detail--content-wrapper" style={{ padding: 16 }}>
          <div
            className="booking-detail--header-mobile"
            style={{
              marginBottom: 16,
              marginLeft: -16,
              marginRight: -16,
            }}
          >
            <BookingDetailHeader
              skeleton={isFetchingServices}
              activeUserId={activeUserId}
              storeActiveUser={userId => this.setActiveUser(userId)}
            />
          </div>

          <BookingServices
            id="section-services"
            skeleton={isFetchingServices}
            onSelectService={service => this.onSelectService(service)}
            isFetchingStylists={isFetchingStylists}
            onSelectStylist={service => this.onSelectStylist(service)}
          />

          {/* Select stylists/date/time are only visible for first user */}
          {/* {activeUser && activeUser.canSelectStylist && (
            <BookingStylists
              id="section-stylists"
              skeleton={isFetchingStylists}
              emptyState={!this.validateServices()}
              onSelectStylist={service => this.onSelectStylist(service)}
            />
          )} */}

          {activeUser && activeUser.canSelectDateTime && (
            <BookingDateTime
              id="section-date-time"
              skeleton={isFetchingDate && isFetchingTime}
              isFetchingDate={isFetchingDate}
              isFetchingTime={isFetchingDate || isFetchingTime}
              emptyState={!this.validateServices() || !this.validateStylists()}
              selectedDate={selectedDate}
              onSelectDate={date => this.onSelectDate(date)}
              selectedTime={selectedTime}
              onSelectTime={time => this.onSelectTime(time)}
            />
          )}
        </div>
      </Scrollbar>
    );
  }

  renderFooter() {
    const { users, activeUserId } = this.props;
    const activeUserIndex = users.findIndex(user => user.id === activeUserId);
    const usersCount = users.length;

    if (activeUserIndex === 0) {
      return (
        <>
          <Button
            size="large"
            className="btn-back"
            icon={<LeftOutlined />}
            onClick={() => this.gotoSelectUsers()}
          >Back</Button>

          {!this.validateServices() && (
            <Button type="primary" size="large" disabled={true}>
              Select services
            </Button>
          )}

          {this.validateServices() && (
            <>
              {!this.validateStylists() && (
                <ScrollIntoView selector="#section-services">
                  <Button
                    type="primary"
                    size="large"
                  >Select stylists</Button>
                </ScrollIntoView>
              )}

              {this.validateStylists() && !this.validateDateTime() && (
                <ScrollIntoView selector="#section-date-time">
                  <Button
                    type="primary"
                    size="large"
                  >Select date & time</Button>
                </ScrollIntoView>
              )}

              {this.validateStylists() && this.validateDateTime() && (
                <>
                  {usersCount > 1 && (
                    <ScrollIntoView selector="#section-services">
                      <Button
                        type="primary"
                        size="large"
                        onClick={() => this.setActiveUser(2)}
                      >Next client 2/{usersCount}</Button>
                    </ScrollIntoView>
                  )}

                  {usersCount === 1 && (
                    <Button
                      type="primary"
                      size="large"
                      onClick={() => this.gotoCheckout()}
                    >Confirm</Button>
                  )}
                </>
              )}
            </>
          )}
        </>
      );
    }

    const currentUserOrder = activeUserIndex + 1;
    const prevUser = users[activeUserIndex - 1];
    const nextUser = users[activeUserIndex + 1];

    return (
      <>
        <ScrollIntoView selector="#section-services">
          <Button
            className="btn-back"
            size="large"
            icon={<LeftOutlined />}
            onClick={() => {
              if (prevUser) {
                this.setActiveUser(prevUser.id);
              }
            }}
          >
            Prev client {currentUserOrder - 1}/{usersCount}
          </Button>
        </ScrollIntoView>

        {!this.validateServices() && (
          <Button type="primary" size="large" disabled={true}>
            Select services
          </Button>
        )}

        {this.validateServices() && (
          <>
            {!this.validateStylists() && (
              <ScrollIntoView selector="#section-services">
                <Button
                  type="primary"
                  size="large"
                >Select stylists</Button>
              </ScrollIntoView>
            )}

            {this.validateStylists() && currentUserOrder < usersCount && (
              <ScrollIntoView selector="#section-services">
                <Button
                  type="primary"
                  size="large"
                  onClick={() => {
                    if (nextUser) {
                      this.setActiveUser(nextUser.id);
                    }
                  }}
                >
                  Next client {currentUserOrder + 1}/{usersCount}
                </Button>
              </ScrollIntoView>
            )}

            {this.validateStylists() && currentUserOrder === usersCount && (
              <Button
                type="primary"
                size="large"
                onClick={() => this.gotoCheckout()}
              >Confirm</Button>
            )}
          </>
        )}
      </>
    );
  }

  render() {
    const { activeUserId } = this.props;
    const { userProfile, isFetchingProfile, isFetchingServices } = this.state;

    return (
      <Layout className="fave-layout booking-detail">
        <Layout.Header className="fave-layout--header booking-detail--header-desktop">
          <BookingDetailHeader
            skeleton={isFetchingServices}
            activeUserId={activeUserId}
            storeActiveUser={userId => this.setActiveUser(userId)}
          />
        </Layout.Header>

        <Layout.Content
          className="fave-layout--content"
          style={{ padding: 0, position: 'relative' }}
        >
          <Layout className="fave-layout booking-detail--content">
            <Layout.Content
              className="fave-layout--content booking-detail--content--container"
              style={{ padding: 0, position: 'relative' }}
            >
              {this.renderContent()}

              <Modal
                title={null}
                footer={null}
                closable={false}
                maskClosable={false}
                visible={!!userProfile}
                wrapClassName="user-profile--modal"
                maskStyle={{ backgroundColor: 'rgba(0, 0, 0, 0.85)' }}
                bodyStyle={{ width: '100%' }}
              >
                <Button
                  shape="circle"
                  className="user-profile--modal-close"
                  icon={<CloseOutlined />}
                  onClick={() => this.setState({ userProfile: null })}
                />
                <UserProfile userProfile={userProfile} />
              </Modal>
            </Layout.Content>

            <Layout.Sider className="fave-layout--slider">
              <div className="booking-detail--content-inner">
                <BookingSummary
                  canViewProfile={true}
                  skeleton={isFetchingServices}
                  isFetchingProfile={isFetchingProfile}
                  fetchUserProfile={options => this.fetchUserProfile(options)}
                />
              </div>
            </Layout.Sider>
          </Layout>
        </Layout.Content>

        <Layout.Footer className="fave-layout--footer">
          <div className="fave-layout--footer-actions">
            {this.renderFooter()}
          </div>
        </Layout.Footer>
      </Layout>
    );
  }
}

const mapStateToProps = state => ({
  users: state.bookingStore.users,
  salonId: state.salonStore.salonId,
  stylistId: state.salonStore.stylistId,
  stylistDate: state.salonStore.date,
  stylistTime: state.salonStore.time,
  bookingId: state.salonStore.bookingId,
  accessToken: state.salonStore.accessToken,
  currentBooking: state.bookingStore.currentBooking,
  activeUserId: state.bookingStore.activeUserId,
  bookingSettings: state.bookingSettings,
});
const mapDispatchToProps = dispatch => ({
  updateStep: (step: number) => dispatch(bookingActions.updateStep(step)),
  updateUsersList: (users: User[]) => dispatch(bookingActions.updateUsers(users)),
  storeActiveUser: (userId: number) => dispatch(bookingActions.storeActiveUser(userId)),
  fetchUserProfile: (options) => dispatch(bookingActions.fetchUserProfile(options)),
  storeBookingNotes: (notes: string) => dispatch(bookingActions.storeBookingNotes(notes)),

  storeActiveRootCateMobile: (cateId: string) => dispatch(bookingActions.storeActiveRootCateMobile(cateId)),
  storeActiveRootCate: (cateId: string) => dispatch(bookingActions.storeActiveRootCate(cateId)),
  storeActiveSubCate: (cateId: string) => dispatch(bookingActions.storeActiveSubCate(cateId)),

  storeStylists: (stylists: Stylist[]) => dispatch(bookingActions.storeStylists(stylists)),
  fetchStylists: (options: FetchStylistOptions, services?: any) => dispatch(bookingActions.fetchStylists(options, services)),
  storeMappedStylists: (options: Record<number, Stylist[]>) => dispatch(bookingActions.storeMappedStylists(options)),

  storeStylistSettings: (settings) => dispatch(bookingActions.storeStylistSettings(settings)),
  fetchStylistSettings: (options: FetchDatesOptions, services?: any) => dispatch(bookingActions.fetchStylistSettings(options, services)),

  storeAvailableTimes: (times: any[]) => dispatch(bookingActions.storeAvailableTimes(times)),
  fetchAvailableTimes: (options: FetchTimesOptions, services?: any) => dispatch(bookingActions.fetchAvailableTimes(options, services)),

  storeSalonServices: (serviceGroups: ServiceGroup[]) => dispatch(salonActions.storeSalonServices(serviceGroups)),
  fetchSalonServices: (salonId: number, stylistId?: number) => dispatch(salonActions.fetchSalonServices(salonId, stylistId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(BookingDetail);
